import axios from 'axios'

const origin = process.env.VUE_APP_API_ORIGIN

const get = path => axios.get(origin + path).then(response => response.data)
const post = (path, payload) => axios.post(origin + path, payload).then(response => response.data)

const PROVIDER_ROLES = [
  'PLATFORM_SUPER_ADMIN',
  'PLATFORM_OPERATIONS_ADMIN',
  'PLATFORM_SUPPORT_VIEWER'
]

function roleValues (context) {
  if (context && Array.isArray(context.roles)) return context.roles
  if (context && Array.isArray(context.ROLES)) return context.ROLES
  return []
}

/**
 * 既存のPlatformContractManagementは初期表示時だけ
 * PLATFORM_SUPER_ADMINの有無を確認している。
 *
 * 実際のルート認可・操作可否はグローバルaccess-contextと
 * accessPolicy / accessManagementUiPolicyを正本にするため、このServiceから
 * 当該Componentへ返す応答にだけ互換Roleを追加する。グローバルキャッシュや
 * バックエンドへは書き戻さず、Operations / SupportをBreak-glass扱いしない。
 */
export function providerComponentContext (data) {
  const context = data && typeof data === 'object' ? data : {}
  const roles = roleValues(context)
    .map(role => String(role || '').trim())
    .filter(Boolean)
  const providerRoles = roles.filter(role => PROVIDER_ROLES.includes(role))

  if (!providerRoles.length || roles.includes('PLATFORM_SUPER_ADMIN')) {
    return context
  }

  return {
    ...context,
    roles: Array.from(new Set([...roles, 'PLATFORM_SUPER_ADMIN'])),
    actualProviderRoles: providerRoles
  }
}

export default {
  getAccessContext () {
    return get('/access-context').then(providerComponentContext)
  },
  getAccounts () {
    return get('/access-management/accounts')
  },
  getPlans () {
    return get('/access-management/plans')
  },
  createAccount (payload) {
    return post('/access-management/accounts', payload)
  },
  /*
   * 契約に関わる会社。**テナントの取引先（クライアント）とは別のもの。**
   *
   *   CONTRACTOR … 契約企業（署名して払う会社）
   *   BILL_TO    … 請求先。空なら契約企業へ請求する
   *   RESELLER   … PREIKA側の販売代理店
   *   AGENT      … お客様側の代行者（社労士・親会社）
   *
   * 保存は**送った区分だけ**を入れ替える。送らなかった区分は触らない——
   * 「代理店だけ直したい」ときに契約企業まで消えては困る。
   */
  getContractParties (accountId) {
    return get(`/access-management/accounts/${accountId}/contract-parties`)
  },
  /*
   * 代理店へ払う手数料の条件。**いまは条件を持つだけ。**
   * 計算も支払もしない（PREIKA運営側に金額が1円も無い）。
   *
   * 保存すると、前の条件は**消さずに閉じる**。いつからいくらだったかが
   * 後から言えないと困る。
   */
  getContractCommission (accountId) {
    return get(`/access-management/accounts/${accountId}/contract-commission`)
  },
  replaceContractCommission (payload) {
    return post('/access-management/contract-commission', payload)
  },
  replaceContractParties (payload) {
    return post('/access-management/contract-parties', payload)
  },
  getOwnerInvitation (accountId) {
    return get(`/access-management/accounts/${accountId}/owner-invitation`)
  },
  resendOwnerInvitation (accountId) {
    return post(`/access-management/accounts/${accountId}/owner-invitation/resend`, {})
  },
  replaceOwnerInvitation (accountId, payload) {
    return post(`/access-management/accounts/${accountId}/owner-invitation/replace`, payload)
  },
  revokeOwnerInvitation (accountId) {
    return post(`/access-management/accounts/${accountId}/owner-invitation/revoke`, {})
  },
  updateContract (payload) {
    return post('/access-management/contract', payload)
  },
  getFeatures (accountId) {
    return get(`/access-management/accounts/${accountId}/features`)
  },
  updateFeature (payload) {
    return post('/access-management/feature', payload)
  },
  getRoles (accountId) {
    return get(`/access-management/accounts/${accountId}/roles`)
  },
  getPermissions () {
    return get('/access-management/permissions')
  },
  createRole (payload) {
    return post('/access-management/role', payload)
  },
  replaceRolePermissions (payload) {
    return post('/access-management/role/permissions', payload)
  },
  getUsers (accountId) {
    return get(`/access-management/accounts/${accountId}/users`)
  },

  /**
   * いま生きている一時パスがあるか（場面 S-95）。
   *
   * **出す前に必ず読む。** 返ってきた livePassId を、そのまま発行の
   * 要求へ添える。添えないと、2人の運営が同時に出したときに**あとから
   * 通ったほうが、先に渡した1本を黙って殺す。**
   *
   * **enabled も返る。** 閉じているときに「出す」を見せると、押しても
   * 必ず断られるボタンになる。「機能はOFF」なのに壊れた機能に見える。
   *
   * **文字列そのものは返ってこない。** DBにも入っていない。
   */
  /**
   * 一時パスの入口が開いているかだけを聞く（場面 S-95）。
   *
   * **対象者が決まる前に読む。** 画面は誰を対象にするか決まる前に、
   * 入口を出すかどうかを決めないといけない。対象者を受け取る口でしか
   * 読めないと、**入口が出ないと押せない、押さないと入口が出ない**
   * という輪になる。実際そうなっていた。
   *
   * **会社も利用者も送らない。** 返るのは enabled ひとつだけ。
   */
  getTemporaryPassStatus () {
    return get('/access-management/temporary-recovery-pass/status')
  },

  getTemporaryPassState (accountId, userId) {
    return get(`/access-management/accounts/${accountId}/users/${userId}/temporary-recovery-pass`)
  },

  /**
   * 一時パスを出す（場面 S-95）。
   *
   * **出せるのは PREIKA運営だけ。** 顧客企業の管理者には出させない。
   * 出せると、会社側の管理者が自分の第2要素を迂回する資格情報を
   * 自分で作れる。
   *
   * **返る文字列は、ここでしか出てこない。** 取り直しはできない。
   * 画面はこれを1度だけ出し、運営が電話で読み上げる。
   * **メールには入らない。**
   */
  issueTemporaryPass (accountId, userId, payload) {
    return post(`/access-management/accounts/${accountId}/users/${userId}/temporary-recovery-pass`, payload)
  },
  assignRole (payload) {
    return post('/access-management/user-role/assign', payload)
  },
  removeRole (payload) {
    return post('/access-management/user-role/remove', payload)
  },
  getAuditLogs (accountId) {
    return get(`/access-management/accounts/${accountId}/audit-logs`)
  }
}
